@if (form) {
  <div class="max-w-4xl mx-auto p-5 bg-white shadow-xl rounded-3xl border border-gray-100">
    <!-- Form Header -->
    <div class="mb-4 border-b border-gray-100 pb-4">
      <div class="flex justify-between items-start">
        <div class="space-y-1">
          <h1 class="text-3xl font-bold text-gray-900 tracking-tight leading-tight">{{ definition.title }}</h1>
          @if (definition.description) {
            <p class="text-base text-gray-500 max-w-2xl leading-relaxed">{{ definition.description }}</p>
          }
        </div>
        @if (allowDraft) {
          <div class="flex space-x-3">
            <button (click)="onSaveDraft()"
                    class="px-5 py-2 text-sm font-semibold text-theme-primary-600 hover:text-theme-primary-700 bg-theme-primary-50 hover:bg-theme-primary-100 rounded-xl transition-all duration-200"
                    type="button">
              Entwurf speichern
            </button>
          </div>
        }
      </div>
    </div>

    <!-- Progress Bar (if multi-page) -->
    @if (definition.pages.length > 1 && !isReviewing) {
      <div class="mb-6">
        <div class="flex items-center justify-between mb-3">
        <span class="text-xs font-bold text-gray-500 uppercase tracking-widest">Schritt {{ currentPageIndex + 1 }}
          von {{ definition.pages.length }}</span>
          <span
            class="text-xs font-bold text-theme-primary-600 uppercase tracking-widest">{{ (currentPageIndex + 1) / definition.pages.length * 100 | number:'1.0-0' }}
            % abgeschlossen</span>
        </div>
        <div class="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
          <div [style.width.%]="(currentPageIndex + 1) / definition.pages.length * 100"
               class="bg-theme-primary-500 h-2 rounded-full transition-all duration-700 ease-out shadow-sm">
          </div>
        </div>
      </div>
    }

    <!-- Multi-page Navigation Pills -->
    @if (definition.pages.length > 1 && !isReviewing) {
      <div class="mb-6 flex space-x-2 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-hide">
        @for (page of definition.pages; track page.id; let i = $index) {
          <button (click)="goToPage(i)"
                  [class.bg-gray-50]="currentPageIndex !== i"
                  [class.bg-theme-primary-600]="currentPageIndex === i"
                  [class.shadow-md]="currentPageIndex === i"
                  [class.shadow-theme-primary-200]="currentPageIndex === i"
                  [class.text-gray-500]="currentPageIndex !== i"
                  [class.text-white]="currentPageIndex === i"
                  class="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap hover:shadow-lg active:scale-95">
            {{ i + 1 }}. {{ page.title }}
          </button>
        }
      </div>
    }

    <form (ngSubmit)="onSubmit()" [formGroup]="form">
      <!--
        Failed-submit summary (#470): names the fields that actually block the submit and where they live, and
        each entry jumps straight to its page. Without it, a required field on a page the employee has already
        left produced no feedback at all — the button simply did nothing.
      -->
      @if (showBlockingBanner) {
        <div class="mb-6 rounded-2xl border border-red-100 bg-red-50/70 p-4 animate-in fade-in slide-in-from-top-2 duration-200"
             role="alert">
          <p class="text-xs font-black text-red-700 uppercase tracking-widest mb-2">Absenden noch nicht möglich</p>
          @if (blockingFields.length) {
            <p class="text-sm font-medium text-red-700 mb-3">
              Diese Angaben fehlen noch oder sind ungültig. Tippen Sie auf einen Eintrag, um direkt dorthin zu
              springen.
            </p>
            <ul class="space-y-1.5">
              @for (field of blockingFields; track $index) {
                <li>
                  <button type="button" (click)="goToPage(field.pageIndex)" [disabled]="field.pageIndex < 0"
                          class="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-semibold text-red-600 transition-colors hover:bg-red-100/70 disabled:cursor-default disabled:hover:bg-transparent">
                    <span class="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-red-400"></span>
                    <span class="min-w-0">
                      <span class="break-words">{{ field.label }}</span>
                      @if (field.pageIndex >= 0) {
                        <span class="block text-xs font-medium text-red-600">
                          Schritt {{ field.pageIndex + 1 }}: {{ field.pageTitle }}
                        </span>
                      }
                    </span>
                  </button>
                </li>
              }
            </ul>
          } @else {
            <p class="text-sm font-medium text-red-700">
              Bitte prüfen Sie Ihre Angaben — einige Eingaben sind noch nicht vollständig.
            </p>
          }
        </div>
      }

      <!-- Cross-field validation summary -->
      @if (crossFieldErrors.length && (form.touched || isReviewing)) {
        <div class="mb-6 rounded-2xl border border-red-100 bg-red-50/70 p-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <p class="text-xs font-black text-red-700 uppercase tracking-widest mb-2">Bitte prüfen</p>
          <ul class="space-y-1">
            @for (msg of crossFieldErrors; track msg) {
              <li class="flex items-start gap-2 text-sm font-semibold text-red-600">
                <span class="mt-1.5 w-1 h-1 rounded-full bg-red-400 flex-shrink-0"></span>{{ msg }}
              </li>
            }
          </ul>
        </div>
      }

      <!-- Current Page Renderer -->
      @if (!isReviewing) {
        <div class="form-content min-h-[400px]">
          <forms-form-page [formGroup]="form" [page]="currentPage"></forms-form-page>
        </div>
      }

      <!-- Review Step -->
      @if (isReviewing) {
        <div class="form-content min-h-[400px] space-y-8 animate-in fade-in duration-500">
          <div class="bg-gray-50/50 rounded-3xl p-6 border border-gray-100">
            <h2 class="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <svg class="h-6 w-6 mr-2 text-theme-primary-500" fill="none" stroke="currentColor"
                   viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2"/>
              </svg>
              Zusammenfassung prüfen
            </h2>

            <div class="space-y-6">
              <div class="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <p class="text-sm text-gray-500 mb-6 leading-relaxed">Bitte überprüfen Sie Ihre Angaben, bevor Sie
                  das Formular endgültig absenden. Mit dem Klick auf „Bestätigen & Absenden“ werden Ihre Daten
                  gespeichert. Fragen, die auf Sie nicht zutreffen, sind hier nicht aufgeführt.</p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  @for (page of definition.pages; track page.id) {
                    <div class="space-y-4">
                      <h3 class="text-xs font-black text-gray-500 uppercase tracking-widest border-b border-gray-50 pb-2">
                        {{ page.title }}
                      </h3>

                      <div class="space-y-3">
                        @for (field of getFlatFields(page.blocks); track $index) {
                          <div class="flex flex-col">
                            <span
                              class="text-[10px] font-bold text-gray-500 uppercase tracking-tight">{{ field.label }}</span>
                            @if (isSignatureImage(field)) {
                              <img [src]="field.value" [alt]="'Unterschrift: ' + field.label"
                                   class="mt-1 h-16 w-auto max-w-full self-start rounded-lg border border-gray-100 bg-white object-contain p-1"/>
                            } @else {
                              <span class="text-sm font-semibold text-gray-900 break-words">{{ field.value || '-' }}</span>
                            }
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>

                <div
                  class="mt-8 flex items-center space-x-3 p-4 bg-theme-primary-50/50 rounded-xl border border-theme-primary-100/50">
                  <div class="p-2 bg-theme-primary-500 text-white rounded-lg">
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path clip-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            fill-rule="evenodd"/>
                    </svg>
                  </div>
                  <span class="text-sm font-bold text-theme-primary-800 tracking-tight">Alle Pflichtfelder wurden korrekt ausgefüllt.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Footer Navigation -->
      <div class="mt-10 flex items-center justify-between border-t border-gray-100 pt-6">
        @if (currentPageIndex > 0 || isReviewing) {
          <button (click)="prevPage()"
                  class="inline-flex items-center px-6 py-3 border border-gray-200 rounded-2xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-semibold shadow-sm transition-all duration-200 active:scale-95"
                  type="button">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"/>
            </svg>
            Zurück
          </button>
        }

        <div class="flex-grow"></div>

        @if (!isReviewing && !isLastPage) {
          <button (click)="nextPage()"
                  class="inline-flex items-center px-8 py-3 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-semibold shadow-lg shadow-theme-primary-100 hover:shadow-xl transition-all duration-200 active:scale-95"
                  type="button">
            Weiter
            <svg class="h-5 w-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"/>
            </svg>
          </button>
        }

        @if (!isReviewing && isLastPage) {
          <button (click)="nextPage()"
                  class="inline-flex items-center px-8 py-3 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-semibold shadow-lg shadow-theme-primary-100 hover:shadow-xl transition-all duration-200 active:scale-95"
                  type="button">
            Prüfen & Absenden
            <svg class="h-5 w-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"/>
            </svg>
          </button>
        }

        @if (isReviewing) {
          <!--
            Bug #485: this used to be `bg-green-500` (white text at 2.27:1, failing WCAG AA's 4.5:1). Submit is
            now the same `theme-primary-600` family as "Weiter"/"Prüfen & Absenden" — color no longer marks this
            as the terminal action, so the distinction rides entirely on shape: the check icon (vs. their
            chevron), the larger `px-10 py-3.5` (vs. `px-8 py-3`), `font-bold` (vs. `font-semibold`), and a
            deeper-tinted `shadow-theme-primary-200` (vs. their `-100`). Keep all four together — dropping any
            one lets this read as just another "next" button.
          -->
          <button [disabled]="form.invalid"
                  class="inline-flex items-center px-10 py-3.5 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-bold shadow-lg shadow-theme-primary-200 hover:shadow-xl transition-all duration-200 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed active:scale-95"
                  type="submit">
            Bestätigen & Absenden
            <svg class="h-5 w-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"/>
            </svg>
          </button>
        }
      </div>
    </form>
  </div>
}
